import { useState, useEffect, type FormEvent } from "react";
import { AuthLayout } from "@/components/AuthLayout";
import { TextField } from "@/components/TextField";
import { SubmitButton } from "@/components/SubmitButton";
import {
  startPasswordless,
  verifyPasswordless,
  type AuthTokens,
} from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

// Passwordless "email me a sign-in code" flow, factored out of LoginPage to keep
// each view small. Two steps: enter email → receive + enter the emailed code.
// Non-enumerating: /start always advances to the code step regardless of whether
// the account exists. On success it hands the tokens back to the login page,
// which resolves /me and routes onward — same path as a password sign-in.

function messageFor(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong. Please try again.";
}

interface Props {
  initialEmail: string;
  onAuthenticated: (tokens: AuthTokens) => Promise<void>;
  onUsePassword: () => void;
  // When entered from the login method step the email is already known, so send
  // the code immediately and start on the code step — no email-confirm step
  // (BRK-S018).
  autoStart?: boolean;
  // /begin already initiated the email-OTP challenge (a code was emailed) and
  // handed back this session — reuse it and start on the code step WITHOUT
  // sending a second code (BRK-S025). Takes precedence over autoStart.
  presentSession?: string;
}

export function EmailOtpLogin({
  initialEmail,
  onAuthenticated,
  onUsePassword,
  autoStart = false,
  presentSession,
}: Props) {
  const [step, setStep] = useState<"email" | "code">(
    presentSession || autoStart ? "code" : "email",
  );
  const [email, setEmail] = useState(initialEmail);
  // With a pre-sent session the username is the email we probed /begin with.
  const [session, setSession] = useState(presentSession ?? "");
  const [username, setUsername] = useState(presentSession ? initialEmail : "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sending, setSending] = useState(autoStart && !presentSession);

  async function requestCode(): Promise<boolean> {
    setError(null);
    setPending(true);
    try {
      const res = await startPasswordless(email);
      setSession(res.session);
      setUsername(res.username);
      return true;
    } catch (err) {
      // Only a real outage rejects /start; surface it and stay on the email step.
      setError(messageFor(err));
      return false;
    } finally {
      setPending(false);
    }
  }

  // Auto-send on mount when launched from the method step (email already known).
  // Skipped when /begin already sent the code (presentSession) — we reuse it.
  useEffect(() => {
    if (presentSession || !autoStart) return;
    let cancelled = false;
    void (async () => {
      await requestCode();
      if (!cancelled) setSending(false);
    })();
    return () => {
      cancelled = true;
    };
    // requestCode reads the initial email; fire exactly once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmitEmail(e: FormEvent) {
    e.preventDefault();
    if (await requestCode()) setStep("code");
  }

  async function onSubmitCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const tokens = await verifyPasswordless({ username, session, code });
      await onAuthenticated(tokens);
    } catch (err) {
      // 401 = the challenge session expired; a fresh code can't revive it, so go
      // back to the email step. 400 = wrong/expired code; stay and let them retry.
      if (err instanceof ApiError && err.status === 401) {
        setStep("email");
        setCode("");
      }
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  }

  if (step === "code") {
    return (
      <AuthLayout
        title="Enter your sign-in code"
        description="We emailed you a sign-in code. Enter it below to sign in."
        footer={
          <button
            type="button"
            onClick={onUsePassword}
            className="text-brk-accent hover:underline"
          >
            Sign in with a password instead
          </button>
        }
      >
        <form className="flex flex-col gap-4" onSubmit={onSubmitCode}>
          {sending && (
            <p role="status" className="text-sm text-brk-dim">
              Sending your sign-in code…
            </p>
          )}
          <TextField
            label="Sign-in code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
          <SubmitButton
            full
            pending={pending && !sending}
            disabled={pending || sending}
            pendingLabel="Verifying…"
          >
            Verify and sign in
          </SubmitButton>
          <div className="flex justify-between text-sm">
            <button
              type="button"
              onClick={() => requestCode()}
              disabled={pending}
              className="text-brk-accent hover:underline disabled:opacity-60"
            >
              Resend code
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
              className="text-brk-accent hover:underline"
            >
              Use a different email
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Email me a sign-in code"
      description="Enter your email and we'll send a one-time code to sign in — no password needed."
      footer={
        <button
          type="button"
          onClick={onUsePassword}
          className="text-brk-accent hover:underline"
        >
          Sign in with a password instead
        </button>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmitEmail}>
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}
        <SubmitButton full pending={pending} pendingLabel="Sending…">
          Email me a code
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
